import {
  AlgoliaProductFetcher,
  formatAlgoliaProductHit,
} from '@lyle/algolia-product-fetcher'
import {
  createMoneyFormatter,
  escapeHtml,
  formatMoney,
  hiddenSalePrice,
  imageUrl,
  isSameProduct,
  isWhiteSwatch,
  metafieldValue,
  parseJson,
} from '@lyle/algolia-product-utils'

const QUICK_VIEW_OPEN_DELAY = 450
const QUANTITY_UPDATE_DELAY = 100
const NEWSLETTER_MODAL_TIMEOUT = 5000
const LOYALTY_CLAIM_TIMEOUT = 8000

class LyleCartDrawer extends HTMLElement {
  #abortController = null
  #claimHandler = claimedReward => this.#handleRewardClaimed(claimedReward)
  #config = null
  #fetcher = null
  #loyaltyObserver = null
  #loyaltyTimer = null
  #moneyFormatter = null
  #newsletterObserver = null
  #newsletterTimer = null
  #openTimer = null
  #popup = null
  #quickView = null
  #quickViewPromise = null
  #quantityAbortController = null
  #recommendationsLoaded = false
  #reconcilingGifts = false
  #restoreDrawerAfterNewsletter = () => {
    requestAnimationFrame(() => {
      if (this.isConnected && !this.#popup.matches(':popover-open')) {
        this.#popup.showPopover()
      }
    })
  }
  #reward = null
  #upsellFallback = null
  #upsellSplide = null

  connectedCallback() {
    this.#config = parseJson(
      this.querySelector('[data-cart-drawer-config]')?.textContent
    )
    this.#popup = this.querySelector('#CartDrawer')

    if (!this.#config || !this.#popup) return

    this.#abortController = new AbortController()
    const { signal } = this.#abortController
    this.#moneyFormatter = createMoneyFormatter(this.#config.market)
    this.#fetcher = new AlgoliaProductFetcher({ config: this.#config, signal })
    this.addEventListener('click', this.#handleClick, { signal })
    this.addEventListener('change', this.#handleChange, { signal })
    this.addEventListener('submit', this.#handleSubmit, { signal })
    this.#popup.addEventListener('toggle', this.#handleToggle, { signal })
    this.querySelector('[data-quick-view-overlay]')?.addEventListener(
      'toggle',
      event => this.#quickView?.handleToggle(event),
      { signal }
    )
    document.addEventListener('click', this.#handleDocumentClick, { signal })
    document.addEventListener('cart:add', this.#handleCartEvent, { signal })
    document.addEventListener('cart:refresh', this.#handleCartEvent, { signal })

    this.#updateCartCounts(this.#config.cart.itemCount)
    this.#initLoyaltyLion()
    void this.#reconcileGifts()

    if (
      new URLSearchParams(window.location.search).get('openCart') === 'true'
    ) {
      this.open()
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort()
    this.#quantityAbortController?.abort()
    this.#loyaltyObserver?.disconnect()
    this.#newsletterObserver?.disconnect()
    this.#upsellSplide?.destroy()
    window.clearTimeout(this.#loyaltyTimer)
    window.clearTimeout(this.#newsletterTimer)
    window.clearTimeout(this.#openTimer)

    if (window._llCartClaimHandler === this.#claimHandler) {
      window._llCartClaimHandler = null
    }

    this.#upsellSplide = null
    this.#upsellFallback = null
  }

  open() {
    if (typeof this.#popup?.showPopover !== 'function') return false

    this.#closeHeaderOverlays()
    if (!this.#popup.matches(':popover-open')) this.#popup.showPopover()
    return true
  }

  #handleDocumentClick = event => {
    const trigger = event.target.closest('[data-cart-drawer-trigger]')

    if (!trigger || typeof this.#popup?.showPopover !== 'function') return

    event.preventDefault()
    this.open()
  }

  #handleCartEvent = event => {
    window.clearTimeout(this.#openTimer)

    if (event.detail?.optimistic) {
      this.#showOptimisticAdd(event.detail.optimistic)
      return
    }

    if (event.detail?.rollback) {
      this.#clearOptimisticAdd()
      this.#popup.hidePopover?.()
      return
    }

    const delay = event.type === 'cart:refresh' ? QUICK_VIEW_OPEN_DELAY : 0
    const sectionHtml = event.detail?.sections?.[this.#config.sectionId]

    this.#openTimer = window.setTimeout(() => {
      void this.#refresh(sectionHtml)
    }, delay)
  }

  #showOptimisticAdd(item) {
    this.#clearOptimisticAdd()
    this.#updateCartCounts(this.#config.cart.itemCount + item.quantity)
    this.querySelector('.cart-drawer__header')?.classList.remove(
      'cart-drawer__header--empty'
    )

    let list = this.querySelector('.cart-drawer__items')

    if (!list) {
      list = document.createElement('ul')
      list.className = 'cart-drawer__items'
      list.setAttribute('data-cart-optimistic-items', '')
      const emptyState = this.querySelector('.cart-drawer__empty')
      emptyState?.setAttribute('data-cart-optimistic-state', '')
      emptyState
        ? emptyState.prepend(list)
        : this.querySelector('[data-cart-error]')?.after(list)
    }

    const options = item.options
      .filter(option => option && option !== 'Default Title')
      .map(option => `<span>${escapeHtml(option)}</span>`)
      .join(
        '<span class="cart-drawer-line-item__separator" aria-hidden="true"></span>'
      )
    const image = item.imageUrl
      ? `<img class="cart-drawer-line-item__image" src="${escapeHtml(item.imageUrl)}" alt="${escapeHtml(item.imageAlt || item.title)}" width="112" height="140">`
      : ''

    list.insertAdjacentHTML(
      'afterbegin',
      `<li class="cart-drawer-line-item" data-cart-optimistic-add>
        <a class="cart-drawer-line-item__image-link" href="${escapeHtml(item.url)}" tabindex="-1" aria-hidden="true">${image}</a>
        <div class="cart-drawer-line-item__content">
          <div class="cart-drawer-line-item__header">
            <div class="cart-drawer-line-item__identity">
              <a class="cart-drawer-line-item__title text-body-sm" href="${escapeHtml(item.url)}">${escapeHtml(item.title)}</a>
              ${options ? `<div class="cart-drawer-line-item__variants text-body-xs">${options}</div>` : ''}
            </div>
            <div class="cart-drawer-line-item__price-group">
              <div class="cart-drawer-line-item__prices text-body-sm"><span class="cart-drawer-line-item__price">${escapeHtml(item.price)}</span></div>
            </div>
          </div>
        </div>
      </li>`
    )
    this.open()
  }

  #clearOptimisticAdd() {
    this.querySelector('[data-cart-optimistic-add]')?.remove()
    this.querySelector('[data-cart-optimistic-items]')?.remove()
    this.querySelector('.cart-drawer__empty')?.removeAttribute(
      'data-cart-optimistic-state'
    )
    this.querySelector('.cart-drawer__header')?.classList.toggle(
      'cart-drawer__header--empty',
      this.#config.cart.itemCount === 0
    )
    this.#updateCartCounts(this.#config.cart.itemCount)
  }

  #handleToggle = () => {
    if (!this.#popup.matches(':popover-open')) return

    this.#closeHeaderOverlays()
    void this.#initUpsell()
    void this.#loadRecommendations()
    this.#syncLoyaltyLion()
  }

  #handleClick = event => {
    const quantityButton = event.target.closest('[data-cart-quantity]')
    const removeButton = event.target.closest('[data-cart-remove]')

    if (
      this.getAttribute('aria-busy') === 'true' &&
      ((!quantityButton && !removeButton) || !this.#quantityAbortController)
    )
      return

    const newsletter = event.target.closest('[data-cart-newsletter]')
    const marketSwitcher = event.target.closest('[data-cart-market-switcher]')
    const loyaltyApply = event.target.closest('[data-cart-loyalty-apply]')
    const quickView = event.target.closest('[data-quick-view]')
    const quickViewClose = event.target.closest('[data-quick-view-close]')
    const quickViewAdd = event.target.closest('[data-quick-view-add]')
    const quickViewArrow = event.target.closest('[data-quick-view-arrow]')
    const quickViewSize = event.target.closest('[data-quick-view-size]')
    const quickViewSwatch = event.target.closest('[data-quick-view-swatch]')
    const upsellSwatch = event.target.closest('[data-cart-upsell-swatch]')

    if (upsellSwatch) {
      this.#selectUpsellSwatch(upsellSwatch)
      return
    }

    if (quickView) {
      void this.#openQuickView(quickView)
      return
    }

    if (quickViewClose) {
      this.#quickView?.close()
      return
    }

    if (quickViewAdd) {
      void this.#quickView?.add(quickViewAdd)
      return
    }

    if (quickViewArrow) {
      this.#quickView?.scrollGallery()
      return
    }

    if (quickViewSize) {
      this.#quickView?.selectVariant(quickViewSize)
      return
    }

    if (quickViewSwatch) {
      void this.#quickView?.selectSwatch(quickViewSwatch)
      return
    }

    if (quantityButton) {
      const line = quantityButton.closest('[data-cart-line]')
      const input = line?.querySelector('[data-cart-quantity-input]')
      if (!line || !input) return

      const change = quantityButton.dataset.cartQuantity === 'increase' ? 1 : -1
      const quantity = Math.max(
        1,
        (Number(input.value) || Number(input.min) || 1) + change
      )
      const max = Number(input.max) || Infinity
      void this.#changeLine(line, Math.min(quantity, max))
      return
    }

    if (removeButton) {
      const line = removeButton.closest('[data-cart-line]')
      if (line) void this.#changeLine(line, 0)
      return
    }

    if (newsletter) {
      this.#openNewsletter(newsletter.dataset.cartNewsletter)
      return
    }

    if (marketSwitcher) {
      this.#popup.hidePopover?.()
      document.querySelector('#MarketSwitcherCountryDrawer')?.showPopover?.()
      return
    }

    if (loyaltyApply) void this.#claimLoyaltyReward()
  }

  async #openQuickView(button) {
    if (!this.#quickViewPromise) {
      this.#quickViewPromise = import('@lyle/algolia-product-card-quick-view')
    }

    const { AlgoliaProductCardQuickView } = await this.#quickViewPromise
    if (!this.isConnected) return

    if (!this.#quickView) {
      this.#quickView = new AlgoliaProductCardQuickView({
        callbacks: {
          cardProduct: card => this.#cardProduct(card),
          familyTag: product => this.#fetcher.familyTag(product),
          fetchProductJson: handle => this.#fetcher.fetchProductJson(handle),
          fetchRelatedProductsBatch: tags =>
            this.#fetcher.fetchRelatedProductsBatch(tags),
          filterColourProducts: (product, relatedProducts) =>
            this.#fetcher.filterColourProducts(product, relatedProducts),
          formatMoney: value => this.#formatMoney(value),
          sendConversionEvent: () => {},
        },
        config: this.#config,
        icons: window.icons || {},
        root: this,
      })
    }

    await this.#quickView.open(button)
  }

  #openNewsletter(formId) {
    this.#newsletterObserver?.disconnect()
    window.clearTimeout(this.#newsletterTimer)

    const promoteModal = () => {
      const modal = document
        .querySelector('[data-testid="POPUP"]')
        ?.closest('[role="dialog"]')
      if (!modal || typeof modal.showPopover !== 'function') return

      modal.setAttribute('popover', 'manual')

      try {
        if (modal.matches(':popover-open')) modal.hidePopover()
        modal.showPopover()
      } catch {
        return
      }

      modal
        .querySelector('button[aria-label="Close dialog"]')
        ?.addEventListener('click', this.#restoreDrawerAfterNewsletter, {
          once: true,
        })
      modal.addEventListener('keydown', event => {
        if (event.key === 'Escape') this.#restoreDrawerAfterNewsletter()
      })

      this.#newsletterObserver?.disconnect()
      this.#newsletterObserver = null
      window.clearTimeout(this.#newsletterTimer)
    }

    this.#newsletterObserver = new MutationObserver(promoteModal)
    this.#newsletterObserver.observe(document.body, {
      childList: true,
      subtree: true,
    })
    this.#newsletterTimer = window.setTimeout(() => {
      this.#newsletterObserver?.disconnect()
      this.#newsletterObserver = null
    }, NEWSLETTER_MODAL_TIMEOUT)

    window._klOnsite = window._klOnsite || []
    window._klOnsite.push(['openForm', formId])
    promoteModal()
  }

  #handleChange = event => {
    const input = event.target.closest('[data-cart-quantity-input]')
    const line = input?.closest('[data-cart-line]')

    if (
      !input ||
      !line ||
      (this.getAttribute('aria-busy') === 'true' &&
        !this.#quantityAbortController)
    )
      return

    const min = Number(input.min) || 1
    const max = Number(input.max) || Infinity
    const quantity = Math.min(Math.max(Number(input.value) || min, min), max)
    void this.#changeLine(line, quantity)
  }

  #handleSubmit = event => {
    const giftForm = event.target.closest('[data-cart-gift-form]')

    if (giftForm) {
      event.preventDefault()
      if (this.getAttribute('aria-busy') !== 'true')
        void this.#addGift(giftForm)
      return
    }

    const form = event.target.closest('[data-cart-discount-form]')
    if (!form) return
    event.preventDefault()
    if (this.getAttribute('aria-busy') === 'true') return
    void this.#applyDiscount(form)
  }

  async #addGift(form) {
    const variantId = Number(new FormData(form).get('id'))
    const giftId = form.dataset.giftId

    if (!variantId || !giftId) return

    this.#setBusy(true)

    try {
      const cart = await this.#cartRequest(this.#config.routes.add, {
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: {
              __gift: giftId,
              __gift_discount: 100,
              __gift_message: this.#config.strings.giftLine,
            },
          },
        ],
      })

      await this.#refresh(cart.sections?.[this.#config.sectionId])
    } catch {
      this.#showError(this.#config.strings.error)
    } finally {
      if (this.isConnected) this.#setBusy(false)
    }
  }

  async #changeLine(line, quantity) {
    const input = line.querySelector('[data-cart-quantity-input]')
    const confirmedQuantity = Number(input?.defaultValue) || 1

    if (input && quantity > 0) this.#setLineQuantity(line, quantity)
    else if (quantity === 0) this.#setLineRemoving(line, true)

    const updates = Object.fromEntries(
      [...this.querySelectorAll('[data-cart-line]')].map(cartLine => [
        cartLine.dataset.lineKey,
        cartLine.hasAttribute('data-cart-removing')
          ? 0
          : Number(
              cartLine.querySelector('[data-cart-quantity-input]')?.value ??
                cartLine.dataset.cartLineQuantity
            ),
      ])
    )

    this.#quantityAbortController?.abort()
    const abortController = new AbortController()
    this.#quantityAbortController = abortController
    this.#setBusy(true)

    try {
      if (quantity > 0) {
        await new Promise(resolve =>
          window.setTimeout(resolve, QUANTITY_UPDATE_DELAY)
        )
        if (abortController.signal.aborted) return
      }

      let cart

      try {
        cart = await this.#cartRequest(
          this.#config.routes.update,
          { updates },
          abortController.signal
        )
      } catch (error) {
        if (error.status !== 422 || !this.querySelector('[data-cart-removing]'))
          throw error

        cart = await this.#retryLineUpdate(updates, abortController.signal)
      }

      this.#updateCartCounts(cart.item_count)
      await this.#refresh(cart.sections?.[this.#config.sectionId])
    } catch {
      if (abortController.signal.aborted) return
      if (input) this.#setLineQuantity(line, confirmedQuantity)
      if (quantity === 0) this.#setLineRemoving(line, false)
      await this.#refresh()
      this.#showError(this.#config.strings.error)
    } finally {
      if (this.#quantityAbortController !== abortController) return
      this.#quantityAbortController = null
      if (this.isConnected) this.#setBusy(false)
    }
  }

  async #retryLineUpdate(updates, signal) {
    const response = await fetch(`${this.#config.routes.cart}.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      signal,
    })

    if (!response.ok) throw new Error('Cart request failed')

    const cart = await response.json()
    const currentKeys = new Set(cart.items.map(item => item.key))
    const rebasedUpdates = Object.fromEntries(
      Object.entries(updates).filter(([key]) => currentKeys.has(key))
    )

    if (!Object.keys(rebasedUpdates).length) return cart
    return this.#cartRequest(
      this.#config.routes.update,
      { updates: rebasedUpdates },
      signal
    )
  }

  #setLineQuantity(line, quantity) {
    const input = line.querySelector('[data-cart-quantity-input]')
    if (!input) return

    input.value = quantity
    line.querySelector('[data-cart-quantity="decrease"]').disabled =
      quantity <= (Number(input.min) || 1)
    line.querySelector('[data-cart-quantity="increase"]').disabled =
      quantity >= (Number(input.max) || Infinity)

    this.#updateOptimisticCount()
  }

  #setLineRemoving(line, removing) {
    line.toggleAttribute('data-cart-removing', removing)
    line.hidden = removing
    this.#updateOptimisticCount()
  }

  #updateOptimisticCount() {
    const itemCount = [...this.querySelectorAll('[data-cart-line]')].reduce(
      (count, line) => {
        const confirmedQuantity = Number(line.dataset.cartLineQuantity)
        const quantity = line.hasAttribute('data-cart-removing')
          ? 0
          : Number(
              line.querySelector('[data-cart-quantity-input]')?.value ??
                confirmedQuantity
            )
        return count + quantity - confirmedQuantity
      },
      this.#config.cart.itemCount
    )
    this.#updateCartCounts(itemCount)
  }

  async #applyDiscount(form, loyalty = null) {
    const details = form?.closest('.cart-drawer__discount')
    const input = form?.querySelector('[data-cart-discount-input]')
    const code = loyalty?.code || input?.value.trim()

    if (!code) return false

    this.#setBusy(true)
    this.#showDiscountMessage('', false, details)

    try {
      const codes = [...new Set([...this.#config.cart.discountCodes, code])]
      const cart = await this.#cartRequest(this.#config.routes.update, {
        discount: codes.join(','),
      })
      const appliedCode = cart.discount_codes?.find(discount => {
        return discount.code?.toLowerCase() === code.toLowerCase()
      })

      if (!appliedCode?.applicable) {
        const message = loyalty
          ? this.#config.strings.loyaltyError
          : form?.dataset.errorMessage || this.#config.strings.discountError
        if (loyalty) this.#showError(message)
        else this.#showDiscountMessage(message, true, details)
        return false
      }

      if (loyalty) {
        try {
          localStorage.setItem(
            'll_redeemed',
            JSON.stringify({ code, reward: loyalty.reward })
          )
        } catch {
          // The applied code remains authoritative when storage is unavailable.
        }
      }

      this.#updateCartCounts(cart.item_count)

      const replacement = await this.#refresh(
        cart.sections?.[this.#config.sectionId]
      )

      if (replacement) {
        replacement.#showDiscountMessage(
          form?.dataset.successMessage || '',
          false
        )
      }

      return Boolean(replacement)
    } catch {
      const message = loyalty
        ? this.#config.strings.loyaltyError
        : form?.dataset.errorMessage || this.#config.strings.discountError
      if (loyalty) this.#showError(message)
      else this.#showDiscountMessage(message, true, details)
      return false
    } finally {
      if (this.isConnected) this.#setBusy(false)
    }
  }

  async #cartRequest(url, body, signal) {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      signal,
      body: JSON.stringify({ ...body, sections: this.#config.sectionId }),
    })
    const cart = await response.json()

    if (!response.ok || cart.status) {
      const error = new Error('Cart request failed')
      error.status = response.status
      throw error
    }
    return cart
  }

  async #refresh(sectionHtml = null) {
    this.#setBusy(true)

    try {
      if (!sectionHtml) {
        const url = new URL(this.#config.routes.cart, window.location.origin)
        url.searchParams.set('section_id', this.#config.sectionId)
        const response = await fetch(url, {
          credentials: 'same-origin',
          headers: { Accept: 'text/html' },
        })

        if (!response.ok) throw new Error('Cart section request failed')
        sectionHtml = await response.text()
      }

      const documentFragment = new DOMParser().parseFromString(
        sectionHtml,
        'text/html'
      )
      const replacement = documentFragment.querySelector('lyle-cart-drawer')
      const nextPopup = replacement?.querySelector('#CartDrawer')
      const currentBody = this.#popup.querySelector('.popup-drawer__body')
      const nextBody = nextPopup?.querySelector('.popup-drawer__body')
      const currentState = currentBody?.querySelector(
        '.cart-drawer__filled, .cart-drawer__empty'
      )
      const nextState = nextBody?.querySelector(
        '.cart-drawer__filled, .cart-drawer__empty'
      )
      const nextConfigScript = replacement?.querySelector(
        '[data-cart-drawer-config]'
      )
      const nextConfig = parseJson(nextConfigScript?.textContent)

      if (
        !currentBody ||
        !nextBody ||
        !currentState ||
        !nextState ||
        !nextConfig
      ) {
        throw new Error('Cart section missing')
      }

      const wasOpen = this.#popup.matches(':popover-open')
      const scrollTop = currentBody.querySelector(
        '.cart-drawer__scroll, .cart-drawer__empty'
      )?.scrollTop
      const currentUpsell = currentBody.querySelector('[data-cart-upsell]')
      const nextUpsell = nextBody.querySelector('[data-cart-upsell]')

      if (currentUpsell && nextUpsell) {
        const currentProducts = currentUpsell.querySelector(
          '[data-cart-upsell-products]'
        )
        const nextProducts = nextUpsell.querySelector(
          '[data-cart-upsell-products]'
        )
        const keepCurrentProducts =
          nextConfig.upsell?.enabled &&
          currentProducts?.querySelector('[data-cart-upsell-card]')

        this.#holdUpsellHeight(currentUpsell)
        this.#upsellFallback = keepCurrentProducts
          ? nextProducts?.cloneNode(true)
          : null
        if (!keepCurrentProducts && currentProducts && nextProducts) {
          currentProducts.replaceChildren(...nextProducts.childNodes)
        }
        currentUpsell.hidden = keepCurrentProducts ? false : nextUpsell.hidden
        nextUpsell.replaceWith(currentUpsell)
      } else {
        this.#upsellSplide?.destroy()
        this.#upsellFallback = null
        this.#upsellSplide = null
      }

      this.#recommendationsLoaded = false
      currentBody.querySelector('[data-cart-optimistic-items]')?.remove()
      currentState.replaceWith(nextState)
      currentBody.querySelector('.cart-drawer__header').className =
        nextBody.querySelector('.cart-drawer__header').className
      const error = currentBody.querySelector('[data-cart-error]')
      error.textContent = ''
      error.hidden = true
      this.#popup.toggleAttribute(
        'scroll-lock',
        nextPopup.hasAttribute('scroll-lock')
      )
      Object.assign(this.#config, nextConfig)
      this.querySelector('[data-cart-drawer-config]').textContent =
        nextConfigScript.textContent

      const count = this.#config.cart.itemCount
      if (Number.isFinite(count)) this.#updateCartCounts(count)

      if (await this.#reconcileGifts()) return this

      if (wasOpen) this.#handleToggle()
      else requestAnimationFrame(() => this.open())

      const nextScroll = currentBody.querySelector(
        '.cart-drawer__scroll, .cart-drawer__empty'
      )
      if (nextScroll && Number.isFinite(scrollTop)) {
        nextScroll.scrollTop = scrollTop
        requestAnimationFrame(() => {
          if (nextScroll.isConnected) nextScroll.scrollTop = scrollTop
        })
      }

      return this
    } catch {
      this.#showError(this.#config.strings.error)
      return null
    } finally {
      if (this.isConnected) this.#setBusy(false)
    }
  }

  async #reconcileGifts() {
    if (this.#reconcilingGifts) return false

    const eligibleGiftIds = new Set(
      [...this.querySelectorAll('[data-cart-gift-eligible]')].map(
        marker => marker.dataset.cartGiftEligible
      )
    )
    const updates = Object.fromEntries(
      [...this.querySelectorAll('[data-cart-line][data-cart-gift-id]')]
        .filter(line => !eligibleGiftIds.has(line.dataset.cartGiftId))
        .map(line => [line.dataset.lineKey, 0])
    )

    if (!Object.keys(updates).length) return false

    this.#reconcilingGifts = true
    this.#setBusy(true)

    try {
      const cart = await this.#cartRequest(this.#config.routes.update, {
        updates,
      })
      await this.#refresh(cart.sections?.[this.#config.sectionId])
      return true
    } catch {
      this.#showError(this.#config.strings.error)
      return false
    } finally {
      this.#reconcilingGifts = false
      if (this.isConnected) this.#setBusy(false)
    }
  }

  #setBusy(busy) {
    this.setAttribute('aria-busy', String(busy))
    const live = this.querySelector('[data-cart-live]')
    if (live) live.textContent = busy ? this.dataset.loadingMessage : ''
  }

  #showError(message) {
    this.#setBusy(false)
    const error = this.querySelector('[data-cart-error]')
    if (!error) return

    error.textContent = message || this.dataset.errorMessage
    error.hidden = false
  }

  #showDiscountMessage(message, isError, details = null) {
    const messageElement = (details || this).querySelector(
      '[data-cart-discount-message]'
    )

    if (!messageElement) return

    messageElement.textContent = message
    messageElement.hidden = !message
    messageElement.classList.toggle('is-error', isError)
  }

  #updateCartCounts(count) {
    document.querySelectorAll('[data-cart-count]').forEach(badge => {
      badge.textContent = count
      badge.hidden = count < 1
    })
  }

  #closeHeaderOverlays() {
    const header = document.querySelector('lyle-site-header')
    header?.dispatchEvent(new CustomEvent('header-search:open'))
    header?.dispatchEvent(new CustomEvent('site-header:menu-open'))
  }

  async #initUpsell() {
    const carousel = this.querySelector('[data-cart-upsell]')
    const slides = carousel?.querySelectorAll('.splide__slide') || []

    if (!carousel || !slides.length) {
      if (carousel) carousel.hidden = true
      this.#upsellSplide?.destroy()
      this.#upsellSplide = null
      return
    }

    carousel.hidden = false
    for (const slide of slides) {
      const product = this.#cardProduct(slide)
      const prices = slide.querySelector('.cart-drawer-upsell-card__price')
      if (product && prices) prices.innerHTML = this.#upsellPriceMarkup(product)
    }
    void this.#hydrateUpsellSwatches()

    const { default: Splide } = await import('@splidejs/splide')
    if (!carousel.isConnected) return

    if (this.#upsellSplide) {
      const index = this.#upsellSplide.index
      this.#upsellSplide.options = { drag: slides.length > 1 }
      this.#upsellSplide.refresh()
      this.#upsellSplide.go(Math.min(index, slides.length - 1))
      return
    }

    const speed = Number(this.#config.upsell?.carouselSpeed)
    const reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches
    const interval = Number.isFinite(speed) ? speed * 1000 : 0
    const autoplay = Boolean(interval && !reducedMotion)

    this.#upsellSplide = new Splide(carousel, {
      arrows: true,
      autoplay,
      drag: true,
      gap: 20,
      interval,
      mediaQuery: 'min',
      padding: { left: 16, right: 16 },
      pagination: false,
      fixedWidth: '70%',
      perMove: 1,
      flickPower: 300,
      dragMinThreshold: {
        mouse: 0,
        touch: 5,
      },
      breakpoints: {
        768: {
          fixedWidth: null,
          padding: { left: 24, right: 24 },
          perPage: 2,
        },
      },
    })

    this.#upsellSplide.mount()
  }

  #holdUpsellHeight(carousel) {
    const height = carousel?.offsetHeight
    if (!height) return

    carousel.style.minHeight = `${height}px`
    requestAnimationFrame(() => {
      requestAnimationFrame(() => carousel.style.removeProperty('min-height'))
    })
  }

  async #loadRecommendations() {
    const recommendation = this.#config.upsell
    const container = this.querySelector('[data-cart-upsell-products]')

    if (this.#recommendationsLoaded) return

    if (
      !recommendation?.enabled ||
      !container ||
      !this.#config.algolia.appId ||
      !this.#config.algolia.apiKey ||
      !this.#config.algolia.indexName
    ) {
      this.#restoreUpsellFallback()
      return
    }

    this.#recommendationsLoaded = true

    try {
      const recommendationProductId = this.#config.cart.lastProductId

      if (
        !recommendationProductId &&
        container.querySelector('[data-cart-upsell-card]') &&
        !this.#upsellFallback
      ) {
        await this.#initUpsell()
        return
      }

      const recommendationModel = recommendationProductId
        ? recommendation.type
        : 'trending-items'
      const queryFilters = String(recommendation.queryFilters || '').trim()
      const fallbackFilters = String(
        recommendation.fallbackFilters || ''
      ).trim()
      const rules = (recommendation.rules || [])
        .map(rule => String(rule || '').trim())
        .filter(Boolean)
      const request = {
        indexName: this.#config.algolia.indexName,
        model: recommendationModel,
        threshold: Number(recommendation.threshold) || 0,
        maxRecommendations: 20,
        queryParameters: {
          filters: queryFilters
            ? `${queryFilters} AND ${this.#config.algolia.defaultFilters}`
            : this.#config.algolia.defaultFilters,
        },
      }

      if (recommendationProductId && recommendationModel !== 'trending-items') {
        request.objectID = String(recommendationProductId)
      }

      if (rules.length) {
        request.queryParameters.ruleContexts = rules
      }

      if (fallbackFilters) {
        request.fallbackParameters = { filters: fallbackFilters }
      }

      const response = await fetch(
        `https://${this.#config.algolia.appId}.algolia.net/1/indexes/*/recommendations`,
        {
          method: 'POST',
          signal: this.#abortController.signal,
          headers: {
            'Content-Type': 'application/json',
            'x-algolia-api-key': this.#config.algolia.apiKey,
            'x-algolia-application-id': this.#config.algolia.appId,
          },
          body: JSON.stringify({ requests: [request] }),
        }
      )

      if (!response.ok) {
        this.#restoreUpsellFallback()
        return
      }

      const data = await response.json()
      const cartProductIds = new Set(this.#config.cart.productIds.map(String))
      const cartProductHandles = new Set(this.#config.cart.productHandles || [])
      const products = (data.results?.[0]?.hits || [])
        .filter(hit => {
          const id = String(hit.productId || hit.id || hit.objectID)
          return !cartProductIds.has(id) && !cartProductHandles.has(hit.handle)
        })
        .slice(0, recommendation.max)
        .map(hit => formatAlgoliaProductHit(hit, this.#config))

      if (!products.length) {
        this.#restoreUpsellFallback()
        return
      }

      const content = document.createElement('template')
      content.innerHTML = products
        .map(product => this.#renderUpsellProduct(product))
        .join('')
      this.#upsellFallback = null
      this.#replaceUpsellProducts(content.content)
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.#recommendationsLoaded = false
        this.#restoreUpsellFallback()
      }
    }
  }

  #restoreUpsellFallback() {
    if (!this.#upsellFallback) return

    const fallback = this.#upsellFallback
    this.#upsellFallback = null
    this.#replaceUpsellProducts(fallback)
  }

  #replaceUpsellProducts(products) {
    const container = this.querySelector('[data-cart-upsell-products]')
    if (!container) return

    const scroll = container.closest(
      '.cart-drawer__scroll, .cart-drawer__empty'
    )
    const scrollTop = scroll?.scrollTop
    this.#holdUpsellHeight(container.closest('[data-cart-upsell]'))
    container.replaceChildren(...products.childNodes)
    void this.#initUpsell()

    if (scroll && Number.isFinite(scrollTop)) {
      scroll.scrollTop = scrollTop
      requestAnimationFrame(() => {
        if (scroll.isConnected) scroll.scrollTop = scrollTop
      })
    }
  }

  #upsellPriceMarkup(product) {
    const salePrice = hiddenSalePrice(product.tags, this.#config.market.currencyCode)
    const originalPrice = Number(product.price?.min ?? product.price?.amount ?? 0)
    const priceAmount = salePrice ?? originalPrice
    const compareAtPriceAmount = salePrice !== null
      ? originalPrice
      : Number(product.compareAtPrice?.min ?? product.compareAtPrice?.amount ?? 0)
    const onSale = compareAtPriceAmount > priceAmount
    const priceOptions = {
      currencySymbol: this.#config.market.currencySymbol,
      formatter: this.#moneyFormatter,
    }
    const price = formatMoney(priceAmount, priceOptions)
    const compareAtPrice = onSale
      ? formatMoney(compareAtPriceAmount, priceOptions)
      : ''
    return onSale
      ? `<span class="product-prices__compare-at-price">${escapeHtml(compareAtPrice)}</span><span class="product-prices__price product-prices__price--on-sale text-body-xs-bold">${escapeHtml(price)}</span>`
      : `<span class="product-prices__price">${escapeHtml(price)}</span>`
  }

  #renderUpsellProduct(product) {
    const image = this.#upsellImage(product)
    const swatch = metafieldValue(product, 'swatch_code')
    const familySize =
      Number(metafieldValue(product, 'colour_family_size')) || 0
    const hiddenSwatches = Math.max(0, familySize - 1)
    const url = escapeHtml(product.url)
    const title = escapeHtml(product.title)
    const bag = window.icons?.['bag-add'] || window.icons?.bag || ''
    const colourLabel = escapeHtml(
      this.#config.strings.colorCount.replace(
        '__count__',
        Math.max(1, familySize)
      )
    )
    const productImageUrl = image ? imageUrl(image, 258) : ''
    const colours = `<div class="cart-drawer-upsell-card__colours" data-cart-upsell-swatches>
      ${swatch ? `<button class="cart-drawer-upsell-card__swatch is-active${isWhiteSwatch(swatch) ? ' cart-drawer-upsell-card__swatch--white' : ''}" type="button" data-cart-upsell-swatch data-image-url="${escapeHtml(productImageUrl)}" data-image-alt="${escapeHtml(image?.alt || product.title)}" aria-label="${title}" aria-current="true"><span class="cart-drawer-upsell-card__swatch-inner" style="--swatch-color: ${escapeHtml(swatch)}"></span></button>` : ''}
      ${hiddenSwatches > 0 ? `<a class="cart-drawer-upsell-card__swatch-count text-eyebrow-2xs" href="${url}" aria-label="${colourLabel}">+${hiddenSwatches}</a>` : ''}
    </div>`

    return `
      <li class="cart-drawer-upsell-card splide__slide" data-cart-upsell-card data-product-card data-product-id="${escapeHtml(product.id)}" data-product-handle="${escapeHtml(product.handle)}" data-product-url="${url}">
        <a class="cart-drawer-upsell-card__image-link" href="${url}" tabindex="-1" aria-hidden="true">
          ${image ? `<img class="cart-drawer-upsell-card__image" src="${escapeHtml(productImageUrl)}" alt="${title}" width="86" height="107" loading="lazy">` : ''}
        </a>
        <div class="cart-drawer-upsell-card__content">
          <div class="cart-drawer-upsell-card__details text-body-xs">
            <a class="cart-drawer-upsell-card__title" href="${url}">${title}</a>
            <span class="cart-drawer-upsell-card__price product-prices">${this.#upsellPriceMarkup(product)}</span>
          </div>

          <div class="cart-drawer-upsell-card__actions">
            ${colours}
            <button class="cart-drawer-upsell-card__add" type="button" data-quick-view aria-label="${escapeHtml(this.#config.strings.quickAdd)}: ${title}">
              <span aria-hidden="true">${bag}</span>
            </button>
          </div>
        </div>
        <script type="application/json" data-product-card-json>${JSON.stringify(product).replace(/</g, '\\u003c')}</script>
      </li>
    `
  }

  async #hydrateUpsellSwatches() {
    const cards = [...this.querySelectorAll('[data-cart-upsell-card]')].filter(
      card =>
        !card.hasAttribute('data-upsell-swatches-loaded') &&
        !card.hasAttribute('data-upsell-swatches-loading')
    )
    const entries = cards
      .map(card => {
        const product = this.#cardProduct(card)
        return { card, product, tag: this.#fetcher.familyTag(product) }
      })
      .filter(({ product, tag }) => product && tag)

    if (!entries.length) return

    entries.forEach(({ card }) =>
      card.setAttribute('data-upsell-swatches-loading', '')
    )

    try {
      const productsByTag = await this.#fetcher.fetchRelatedProductsBatch(
        entries.map(({ tag }) => tag)
      )

      entries.forEach(({ card, product, tag }) => {
        const swatches = this.#fetcher.filterColourProducts(
          product,
          productsByTag.get(tag) || []
        )
        const visible = this.#visibleUpsellSwatches(swatches, product)
        const container = card.querySelector('[data-cart-upsell-swatches]')

        if (container && visible.length) {
          const hiddenCount = swatches.length - visible.length
          const colourLabel = escapeHtml(
            this.#config.strings.colorCount.replace(
              '__count__',
              swatches.length
            )
          )

          container.innerHTML = `${visible
            .map(swatch => this.#upsellSwatchMarkup(swatch, product))
            .join('')}${
            hiddenCount > 0
              ? `<a class="cart-drawer-upsell-card__swatch-count text-eyebrow-2xs" href="${escapeHtml(product.url)}" aria-label="${colourLabel}">+${hiddenCount}</a>`
              : ''
          }`
        }

        card.removeAttribute('data-upsell-swatches-loading')
        card.setAttribute('data-upsell-swatches-loaded', '')
      })
    } catch {
      entries.forEach(({ card }) =>
        card.removeAttribute('data-upsell-swatches-loading')
      )
    }
  }

  #visibleUpsellSwatches(swatches, product) {
    const visible = swatches.slice(0, 3)
    const active = swatches.find(swatch => isSameProduct(swatch, product))

    if (!active || visible.some(swatch => isSameProduct(swatch, product))) {
      return visible
    }

    return [
      active,
      ...swatches.filter(swatch => !isSameProduct(swatch, active)),
    ].slice(0, 3)
  }

  #upsellSwatchMarkup(swatch, product) {
    const active = isSameProduct(swatch, product)
    const colour = metafieldValue(swatch, 'swatch_code') || '#ffffff'
    const image = this.#upsellImage(swatch)
    const imageSrc = image ? imageUrl(image, 258) : ''

    return `<button class="cart-drawer-upsell-card__swatch${active ? ' is-active' : ''}${isWhiteSwatch(colour) ? ' cart-drawer-upsell-card__swatch--white' : ''}" type="button" data-cart-upsell-swatch data-product='${escapeHtml(JSON.stringify(swatch))}' data-image-url="${escapeHtml(imageSrc)}" data-image-alt="${escapeHtml(image?.alt || swatch.title)}" aria-label="${escapeHtml(swatch.title)}"${active ? ' aria-current="true"' : ''}><span class="cart-drawer-upsell-card__swatch-inner" style="--swatch-color: ${escapeHtml(colour)}"></span></button>`
  }

  #selectUpsellSwatch(swatch) {
    const card = swatch.closest('[data-cart-upsell-card]')
    const image = card?.querySelector('.cart-drawer-upsell-card__image')
    if (!image || !swatch.dataset.imageUrl) return

    image.removeAttribute('srcset')
    image.removeAttribute('sizes')
    image.src = swatch.dataset.imageUrl
    image.alt = swatch.dataset.imageAlt || ''

    card.querySelectorAll('[data-cart-upsell-swatch]').forEach(button => {
      const active = button === swatch
      button.classList.toggle('is-active', active)
      button.toggleAttribute('aria-current', active)
    })
  }

  #upsellImage(product) {
    const images = product.images || []
    const flatLay = images.find(image =>
      image.alt?.toLowerCase().includes('flat-lay')
    )
    if (flatLay) return flatLay

    const altLanding = images.find(image =>
      image.alt?.includes('alt_landing_image')
    )
    if (this.#config.productCard.useAltImage && altLanding) return altLanding

    const standard = images.filter(
      image => !image.alt?.includes('alt_landing_image')
    )
    if (this.#config.productCard.imageOrder === 'flat')
      return standard.at(-1) || images[0]
    return standard[0] || images[0]
  }

  #cardProduct(card) {
    return parseJson(
      card?.querySelector('[data-cart-upsell-swatch].is-active[data-product]')
        ?.dataset.product ||
        card?.querySelector('[data-product-card-json]')?.textContent
    )
  }

  #formatMoney(value) {
    return formatMoney(value, {
      currencySymbol: this.#config.market.currencySymbol,
      formatter: this.#moneyFormatter,
    })
  }

  #initLoyaltyLion() {
    if (!this.#config.customer || !this.#config.loyaltyRewards.length) return

    const onReady = () => {
      if (!this.isConnected) return
      this.#registerLoyaltyHook()
      this.#syncLoyaltyLion()
    }

    if (window._llReady || window.loyaltylion?.customer) {
      onReady()
    } else if (typeof window.loyaltylion?.on === 'function') {
      window.loyaltylion.on('sdkReady', onReady)
    } else {
      window._llReadyCallbacks = window._llReadyCallbacks || []
      window._llReadyCallbacks.push(onReady)
    }

    const rewardsList = document.querySelector('#ll-rewards-list')
    if (rewardsList && typeof MutationObserver !== 'undefined') {
      this.#loyaltyObserver = new MutationObserver(() =>
        this.#syncLoyaltyLion()
      )
      this.#loyaltyObserver.observe(rewardsList, {
        childList: true,
        subtree: true,
      })
    }
  }

  #registerLoyaltyHook() {
    window._llCartClaimHandler = this.#claimHandler

    if (
      window._llCartClaimHookRegistered ||
      typeof window.loyaltylion?.registerHook !== 'function'
    ) {
      return
    }

    window._llCartClaimHookRegistered = true
    window.loyaltylion.registerHook(
      'customers.rewards.claimed',
      ({ claimedReward }) => {
        void window._llCartClaimHandler?.(claimedReward)
        return { action: 'hide_claim_modal' }
      }
    )
  }

  #syncLoyaltyLion() {
    const loyalty = this.querySelector('[data-cart-loyalty]')
    const earned = this.querySelector('[data-cart-points-earned]')

    if (!loyalty || !earned) return

    const tierId = window._llCustomerTierId
    const tierRate = Number(window._llTierPointsMap?.[String(tierId)]) || 3
    const pointsEarned = Math.round(
      (this.#config.cart.totalPrice / 100) * tierRate
    )
    earned.textContent = `${pointsEarned} ${this.#config.strings.points}`

    const stored = this.#storedLoyaltyReward()
    if (
      stored?.code &&
      this.#config.cart.discountCodes.some(code => {
        return code.toLowerCase() === stored.code.toLowerCase()
      })
    ) {
      this.#renderLoyaltyMessage(
        this.#config.strings.pointsRedeemed,
        stored.reward,
        false
      )
      return
    }

    const balance = Number(window.loyaltylion?.customer?.pointsApproved) || 0
    const rewards = [...this.#config.loyaltyRewards].sort(
      (first, second) => second.points - first.points
    )
    this.#reward = rewards.find(reward => {
      if (!reward.rewardId || balance < reward.points) return false
      const button = this.#loyaltyButton(reward.rewardId)
      return (
        button && !button.classList.contains('lion-action-button--disabled')
      )
    })

    if (!this.#reward) {
      loyalty.hidden = true
      return
    }

    this.#renderLoyaltyMessage(
      this.#config.strings.pointsRedeem,
      this.#reward,
      true
    )
  }

  #renderLoyaltyMessage(template, reward, showApply) {
    const loyalty = this.querySelector('[data-cart-loyalty]')
    const message = loyalty?.querySelector('[data-cart-loyalty-message]')
    const apply = loyalty?.querySelector('[data-cart-loyalty-apply]')

    if (!loyalty || !message || !apply || !reward) return

    const amount = new Intl.NumberFormat(
      this.#config.currency.language || document.documentElement.lang,
      {
        style: 'currency',
        currency: this.#config.currency.code,
      }
    ).format(reward.amount * (Number(window.Shopify?.currency?.rate) || 1))

    const values = {
      '{{ points }}': String(reward.points),
      '{{ amount }}': amount,
    }
    message.replaceChildren(
      ...template.split(/(\{\{ points \}\}|\{\{ amount \}\})/).map(part => {
        if (!values[part]) return document.createTextNode(part)
        const strong = document.createElement('strong')
        strong.textContent = values[part]
        return strong
      })
    )
    apply.hidden = !showApply
    loyalty.hidden = false

    const color = window._llTierColorMap?.[String(window._llCustomerTierId)]
    if (color) loyalty.style.backgroundColor = color
  }

  #loyaltyButton(rewardId) {
    return document.querySelector(
      `#ll-rewards-list [data-reward-id="${CSS.escape(String(rewardId))}"] button`
    )
  }

  #storedLoyaltyReward() {
    try {
      return JSON.parse(localStorage.getItem('ll_redeemed') || 'null')
    } catch {
      localStorage.removeItem('ll_redeemed')
      return null
    }
  }

  #claimLoyaltyReward() {
    const button = this.#loyaltyButton(this.#reward?.rewardId)

    if (!button || button.classList.contains('lion-action-button--disabled')) {
      this.#showError(this.#config.strings.loyaltyError)
      return
    }

    this.#setBusy(true)
    window.clearTimeout(this.#loyaltyTimer)
    this.#loyaltyTimer = window.setTimeout(() => {
      if (!this.isConnected) return
      this.#showError(this.#config.strings.loyaltyError)
    }, LOYALTY_CLAIM_TIMEOUT)
    button.click()
  }

  async #handleRewardClaimed(claimedReward) {
    window.clearTimeout(this.#loyaltyTimer)
    const code = claimedReward?.redeemable?.code

    if (!code || !this.#reward) {
      this.#showError(this.#config.strings.loyaltyError)
      return
    }

    await this.#applyDiscount(null, {
      code,
      reward: this.#reward,
    })
  }
}

if (!customElements.get('lyle-cart-drawer')) {
  customElements.define('lyle-cart-drawer', LyleCartDrawer)
}
